import type { MarketingBundle } from "@/lib/marketing-attribution";

import { PaymentProcessor } from "@/types/auth";
import { Category } from "@/types/category";
import { ItemType } from "@/types/enums/item-type";
import { MembershipShopBillingStart } from "@/types/enums/membership";
import { ShopFulfillmentMethod } from "@/types/enums/shop-fulfillment-method";
import { Item } from "@/types/inventory/item";
import type { ChargeSavedMethodResponse } from "@/types/payment";

export interface ShopChargeSavedMethodResponse extends ChargeSavedMethodResponse {
  /** The same checkout was already accepted; status still reflects settlement. */
  recovered?: boolean;
}

/**
 * Item shape returned by the unified shop catalog endpoints. Aligned with
 * `Item` (back-office) for the fields a shopper sees; `description` and `imageUrl`
 * are computed server-side for the public surface and explicitly nullable on
 * the wire.
 */
export interface PackageIncludedItem {
  itemId: string | null;
  name: string;
  description: string | null;
  imageUrl: string | null;
  unitPrice: number | null;
  quantity: number;
  retailTotal: number | null;
}

export type PublicShopPromotionConditionType =
  | "ITEM_REQUIRED"
  | "MIN_QUANTITY"
  | "MIN_SUBTOTAL";

export interface PublicShopPromotionCondition {
  ruleType: PublicShopPromotionConditionType;
  group: number;
  itemId: string | null;
  itemName: string | null;
  minQuantity: number | null;
  minSubtotalCents: number | null;
}

export interface PublicShopPromotion {
  id: string;
  offerId: string;
  name: string;
  description: string | null;
  percentage: number | null;
  amount: number | null;
  quantity: number | null;
  targetDescription: string | null;
  targetItemId: string | null;
  targetItemType: ItemType | null;
  targetCategory: string | null;
  targetItemIds: string[];
  conditions: PublicShopPromotionCondition[];
  showConditionMessaging: boolean;
  /** Set on a free item hidden from the shop; the shopper adds it from checkout once the cart qualifies. */
  giftItemName?: string | null;
  giftItemImageUrl?: string | null;
  giftItemPriceCents?: number | null;
}

export interface ShopMembershipForm {
  id: string;
  name: string;
  pathName: string;
  completed: boolean;
}

/** A run of membership billing cycles that all cost the same. */
export interface MembershipCyclePrice {
  fromCycle: number;
  /** null means every cycle from `fromCycle` on repeats this price. */
  toCycle: number | null;
  /** Cycle price in cents, excluding any one-time setup fee. */
  price: number;
}

export interface ShopItem extends Pick<
  Item,
  | "id"
  | "name"
  | "price"
  | "type"
  | "category"
  | "sortOrder"
  | "shipsInOnlineShop"
> {
  description: string | null;
  imageUrl: string | null;
  allowCustomPricing?: boolean;
  /** Gift cards only: what the card may be redeemed for. Null on open-value cards. */
  giftCardRestrictionLabel?: string | null;
  /** Display price after public preview promotions, when supplied. */
  displayPrice?: number | null;
  originalPrice?: number | null;
  applicablePromotions?: PublicShopPromotion[];
  availablePromotions?: PublicShopPromotion[];
  bestPromotion?: PublicShopPromotion | null;
  packageSavings?: number | null;
  packageIncludedItems?: PackageIncludedItem[] | null;
  requiresPickupLocation?: boolean;
  // Memberships only: the locations that may sell it; null sells everywhere.
  soldAtLocationIds?: string[] | null;
  setupFee?: number | null;
  recurringPrice?: number | null;
  membershipCyclePricing?: MembershipCyclePrice[] | null;
  billingFrequency?: string | null;
  autoRenew?: boolean | null;
  shopBillingStart?: MembershipShopBillingStart | null;
  form?: ShopMembershipForm | null;
  /** Memberships only: the signed-in patient already holds it, so it can't be bought again. */
  alreadyMember?: boolean;
  /** Photo URLs in display order; the first is the cover when one is set. */
  galleryImageUrls?: string[];
  /** Staff marked this item popular; it is badged and listed first. */
  isPopular?: boolean;
}

export function isCustomAmountGiftCard(
  item: Pick<ShopItem, "type" | "price" | "allowCustomPricing">,
): boolean {
  return (
    item.type === ItemType.GIFT_CARD &&
    item.price === 0 &&
    item.allowCustomPricing === true
  );
}

export interface ShopCategory extends Category {
  depth: number;
  order: number;
  isSynthetic?: boolean;
  isOther?: boolean;
}

export interface ShopCatalogApiItem extends Omit<
  ShopItem,
  "applicablePromotions" | "availablePromotions" | "bestPromotion"
> {
  applicableOfferIds: string[];
  availableOfferIds: string[];
  bestOfferId: string | null;
}

/** Normalized wire contract returned by the shop catalog endpoints. */
export interface ShopShippingPolicy {
  feeCents: number;
  /** Shipping is free only when the shippable merchandise subtotal exceeds this amount; null means never free. */
  freeShippingOverCents: number | null;
}

export interface ShopCatalogApiResponse {
  items: ShopCatalogApiItem[];
  categories: ShopCategory[];
  offers: PublicShopPromotion[];
  shipping?: ShopShippingPolicy | null;
}

/** Resolved client view used by existing catalog presentation components. */
export interface ShopCatalogResponse {
  items: ShopItem[];
  categories: ShopCategory[];
  promotions: PublicShopPromotion[];
  shipping?: ShopShippingPolicy | null;
}

export type ShopManagementVisibility = "visible" | "hidden" | "all";

export interface ShopManagementCatalogFilters {
  page: number;
  perPage: number;
  search?: string;
  category?: string | null;
  visibility?: ShopManagementVisibility;
  itemType?: ItemType | ItemType[] | string | string[] | null;
}

export interface ShopManagementRow extends ShopItem {
  /** Shop-owned display position. `sortOrder` stays inventory-owned. */
  shopRank: number | null;
  hiddenInOnlineShop: boolean;
  canAppearInShop: boolean;
  inPublicCatalog: boolean;
  packageSavings?: number | null;
  packageIncludedItems?: PackageIncludedItem[] | null;
  requiresPickupLocation?: boolean;
  setupFee?: number | null;
  recurringPrice?: number | null;
  membershipCyclePricing?: MembershipCyclePrice[] | null;
  billingFrequency?: string | null;
  autoRenew?: boolean | null;
  shopBillingStart?: MembershipShopBillingStart | null;
  form?: ShopMembershipForm | null;
}

export interface ShopManagementCatalogResponse {
  data: ShopManagementRow[];
  total: number;
  page: number;
  perPage: number;
  categories: ShopCategory[];
  visibleTotal: number;
  hiddenTotal: number;
}

export interface ShopManagementMoveRequest {
  scope: { itemIds: string[] };
  beforeItemId?: string | null;
  afterItemId?: string | null;
}

export interface ShopManagementMoveResponse {
  movedItem?: ShopManagementRow;
  affectedRows?: ShopManagementRow[];
}

export interface GiftCardRecipientInput {
  firstName?: string | null;
  lastName?: string | null;
  email?: string | null;
  phone?: string | null;
  note?: string | null;
  /** ISO datetime. `null` = send immediately on payment. */
  scheduledSendAt?: string | null;
}

/**
 * Base wire-line for an online purchase. Future item types extend this with
 * their own per-line fields (e.g. accepted terms for memberships, ticket
 * slot for events) rather than piling them onto the primitive — the server
 * dispatches per `itemType` to a type-specific handler.
 */
export interface PurchaseLineInput {
  itemType: ItemType;
  quantity: number;
}

export interface GiftCardPurchaseLineInput extends PurchaseLineInput {
  itemType: ItemType.GIFT_CARD;
  /** Preset SKU id. Mutually exclusive with `amountCents`. */
  itemId?: string;
  /** Custom dollar amount in cents. Mutually exclusive with `itemId`. */
  amountCents?: number;
  recipients?: GiftCardRecipientInput[] | null;
}

export interface PackagePurchaseLineInput extends PurchaseLineInput {
  itemType: ItemType.PACKAGE;
  itemId: string;
  /** Client-seen unit price; the API returns a stale-cart 409 if it changed. */
  expectedUnitPriceCents?: number;
}

export interface MembershipPurchaseLineInput extends PurchaseLineInput {
  itemType: ItemType.MEMBERSHIP;
  itemId: string;
  quantity: 1;
  expectedUnitPriceCents: number;
  expectedRecurringPriceCents: number | null;
  expectedBillingFrequency: string | null;
  expectedAutoRenew: boolean | null;
  expectedShopBillingStart: MembershipShopBillingStart | null;
}

export type RegularCatalogItemType =
  | ItemType.SERVICE
  | ItemType.PRODUCT
  | ItemType.MEDICATION;

export type RegularCatalogShopItem = Omit<ShopItem, "type"> & {
  type: RegularCatalogItemType;
};

export interface RegularCatalogPurchaseLineInput extends PurchaseLineInput {
  itemType: RegularCatalogItemType;
  itemId: string;
  /** Client-seen unit price; the API returns a stale-cart 409 if it changed. */
  expectedUnitPriceCents?: number;
}

export type ShopPurchaseLineInput =
  | GiftCardPurchaseLineInput
  | PackagePurchaseLineInput
  | MembershipPurchaseLineInput
  | RegularCatalogPurchaseLineInput;

export interface EnsureMembershipFormEnrollmentRequest {
  membershipId: string;
  pickupLocationId?: string;
}

export interface EnsureMembershipFormEnrollmentResponse {
  memberId: string;
}

export interface CreatePurchasePayinConfigRequest {
  patientId: string;
  items: ShopPurchaseLineInput[];
  /** Required when purchasing packages; used as the pickup/tax location. */
  pickupLocationId?: string;
  /** How ship-eligible items reach the patient; omitted ships them when shipping is on. */
  fulfillmentMethod?: ShopFulfillmentMethod;
  /** Stable retry key so completed zero-total shop purchases are not fulfilled twice. */
  checkoutIdempotencyKey?: string;
  /** Exact patient-credit amount to apply before charging the card. */
  patientCreditAmountCents?: number;
  isSurcharged?: boolean;
  /**
   * The shopper's marketing journey. Attached by the shop API client rather
   * than by callers, so every checkout path carries it.
   */
  marketing?: MarketingBundle;
}

export interface AppliedShopPromotion {
  id: string;
  name: string;
  description: string | null;
  discountCents: number;
}

export interface ShopPurchasePricingResponse {
  total: number;
  subtotalCents: number;
  discountTotalCents: number;
  taxTotalCents: number;
  shippingFeeCents?: number;
  /** Patient credit already committed to this checkout. */
  appliedPatientCreditCents: number;
  /** Amount still outstanding on the card leg. */
  cardAmountDueCents: number;
  /** Current usable balance computed by the server; display-only. */
  availablePatientCreditCents: number;
  appliedPromotions: AppliedShopPromotion[];
}

export type CreatePurchasePayinConfigResponse = ShopPurchasePricingResponse & {
  completed: boolean;
  sessionKey: string;
  payinConfigId: string;
  /** Mirrors the `sandbox` flag self-schedule's payin-config returns. */
  sandbox: boolean;
  /**
   * Which processor minted this payin. `payinResponseToSession` already read
   * these four; the server just never sent them, so on a Stripe merchant the
   * card form had nothing to load.
   */
  processor?: PaymentProcessor;
  publishableKey?: string | null;
  stripeAccount?: string | null;
  clientSecret?: string | null;
};

/** The server derives the amount and credit rows from this cart request. */
export type CompletePurchaseWithPatientCreditRequest = Omit<
  CreatePurchasePayinConfigRequest,
  "isSurcharged"
>;

export interface CompletePurchaseWithPatientCreditResponse {
  completed: true;
  paymentId: string;
  /** Original charge when this checkout was already accepted by any method. */
  chargeId?: string | null;
  amount: number;
  patientCreditBalanceRemainingCents: number;
}

/**
 * Slim buyer shape that both auth surfaces (MFA OTP-gated public route, and
 * the Firebase-authed patient portal) can produce. Page-level components
 * depend on this shape, never on the underlying store.
 */
export interface PurchaseBuyer {
  id: string;
  firstName: string;
  lastName: string | null;
  phoneNumber: string | null;
  email: string | null;
  /** Preferred/primary clinic location for buyer-facing location suggestions. */
  locationId?: string | null;
  primaryLocationId?: string | null;
  surchargeDisabled?: boolean;
  address?: string | null;
  addressLineTwo?: string | null;
  city?: string | null;
  state?: string | null;
  zipCode?: string | null;
}

export interface PurchaseAuth {
  buyer: PurchaseBuyer | null;
  isAuthenticated: boolean;
  isBuyerLoading?: boolean;
  /**
   * Triggers whatever flow is appropriate for this surface to authenticate
   * the buyer. On `/shop` this opens the OTP modal; in the patient portal
   * it's a no-op because the layout already gates entry.
   */
  ensureAuthenticated: () => void | Promise<void>;
}

/**
 * Snapshot stored on a shop-cart line so the cart survives mid-session
 * price/name changes (the server revalidates on checkout). Keep this slim —
 * it goes to localStorage. Distinct from the back-office `CartItem` in
 * `types/cart.ts`; this is the online-purchase client-side cart.
 */
export interface ShopCartItemSnapshot {
  name: string;
  description: string | null;
  imageUrl?: string | null;
  category?: string | null;
  packageSavings?: number | null;
  packageIncludedItems?: PackageIncludedItem[] | null;
  requiresPickupLocation?: boolean;
  membership?: {
    setupFee?: number | null;
    recurringPrice?: number | null;
    billingFrequency?: string | null;
    autoRenew?: boolean | null;
    shopBillingStart?: MembershipShopBillingStart | null;
    form?: ShopMembershipForm | null;
  };
}

/**
 * Discriminated by `kind`:
 *   - `preset` lines reference a real `GiftCardItem` SKU
 *   - `custom` lines carry a buyer-specified amount; the server creates a
 *     synthetic archived `GiftCardItem` at checkout time.
 */
export type GiftCardShopCartLine =
  | {
      kind: "preset";
      lineId: string;
      itemType: ItemType.GIFT_CARD;
      itemId: string;
      unitPriceCents: number;
      quantity: number;
      recipients: GiftCardRecipientInput[]; // 0 ≤ length ≤ quantity (remainder kept by buyer)
      snapshot: ShopCartItemSnapshot;
    }
  | {
      kind: "custom";
      lineId: string;
      itemType: ItemType.GIFT_CARD;
      amountCents: number;
      quantity: number;
      recipients: GiftCardRecipientInput[];
      snapshot: ShopCartItemSnapshot;
    };

export type PackageShopCartLine = {
  kind: "package";
  lineId: string;
  itemType: ItemType.PACKAGE;
  itemId: string;
  /** Base catalog price; promotions are resolved for the current viewer. */
  unitPriceCents: number;
  /** Original catalog price used only for server-side stale-price validation. */
  expectedUnitPriceCents?: number;
  quantity: number;
  snapshot: ShopCartItemSnapshot;
};

export type RegularCatalogShopCartLine = {
  kind: "catalog-item";
  lineId: string;
  itemType: RegularCatalogItemType;
  itemId: string;
  /** Base catalog price; promotions are resolved for the current viewer. */
  unitPriceCents: number;
  /** Original catalog price used only for server-side stale-price validation. */
  expectedUnitPriceCents?: number;
  quantity: number;
  /** Added from a free-gift offer; dropped when that offer leaves the catalog. */
  freeGift?: true;
  snapshot: ShopCartItemSnapshot;
};

export type MembershipShopCartLine = {
  kind: "membership";
  lineId: string;
  itemType: ItemType.MEMBERSHIP;
  itemId: string;
  unitPriceCents: number;
  expectedUnitPriceCents?: number;
  quantity: 1;
  form: ShopMembershipForm | null;
  snapshot: ShopCartItemSnapshot;
};

/**
 * Future kinds (MEMBERSHIP, EVENTS) widen this union without touching the
 * cart hook itself — the strategy registry knows how to configure / render /
 * validate each kind.
 */
export type ShopCartLine =
  | GiftCardShopCartLine
  | PackageShopCartLine
  | MembershipShopCartLine
  | RegularCatalogShopCartLine;

/** Order-level charges added on top of merchandise at payment. */
export interface PurchaseCharges {
  taxCents: number;
  shippingCents: number;
}

export interface ShopCartState {
  tenantSlug: string;
  lines: ShopCartLine[];
  /** Persists retry identity until this cart session is emptied or completed. */
  checkoutNonce?: string;
}
